import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { X, Loader2, ChevronLeft, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { getTemplates, createFromTemplate } from '@/api/policies';
import type { PolicyTemplate } from '@/api/policies';

interface PolicyTemplateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type DialogState = 'select' | 'configure' | 'creating';

const categoryBadgeClass: Record<string, string> = {
  rbac: 'bg-status-info/10 text-status-info',
  abac: 'bg-plan-growth/10 text-plan-growth',
  deny: 'bg-status-deny/10 text-status-deny',
};

const effectBadgeClass: Record<string, string> = {
  allow: 'bg-status-allow/10 text-status-allow',
  deny: 'bg-status-deny/10 text-status-deny',
};

function CategoryBadge({ category }: { category: string }) {
  const cls = categoryBadgeClass[category.toLowerCase()] ?? 'bg-muted text-muted-foreground';
  return (
    <span className={`text-xs font-medium rounded px-1.5 py-0.5 ${cls}`}>
      {category.toUpperCase()}
    </span>
  );
}

function EffectBadge({ effect }: { effect: string }) {
  const cls = effectBadgeClass[effect.toLowerCase()] ?? 'bg-muted text-muted-foreground';
  return (
    <span className={`text-xs font-medium rounded px-1.5 py-0.5 ${cls}`}>
      {effect}
    </span>
  );
}

export function PolicyTemplateDialog({ open, onOpenChange }: PolicyTemplateDialogProps) {
  const queryClient = useQueryClient();
  const [dialogState, setDialogState] = useState<DialogState>('select');
  const [selectedTemplate, setSelectedTemplate] = useState<PolicyTemplate | null>(null);
  const [placeholderValues, setPlaceholderValues] = useState<Record<string, string>>({});
  const [policyName, setPolicyName] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);

  const { data: templates, isLoading } = useQuery({
    queryKey: ['policy-templates'],
    queryFn: getTemplates,
    enabled: open,
  });

  const createMutation = useMutation({
    mutationFn: createFromTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['policies'] });
      handleClose();
    },
    onError: (err) => {
      setCreateError(err instanceof Error ? err.message : 'Failed to create policy');
      setDialogState('configure');
    },
  });

  function handleClose() {
    onOpenChange(false);
    setDialogState('select');
    setSelectedTemplate(null);
    setPlaceholderValues({});
    setPolicyName('');
    setCreateError(null);
  }

  function handleSelectTemplate(template: PolicyTemplate) {
    setSelectedTemplate(template);
    setPolicyName(template.name);
    const initial: Record<string, string> = {};
    template.placeholders.forEach((p) => { initial[p.key] = ''; });
    setPlaceholderValues(initial);
    setDialogState('configure');
    setCreateError(null);
  }

  function handleBack() {
    setDialogState('select');
    setSelectedTemplate(null);
    setCreateError(null);
  }

  function handleCreate() {
    if (!selectedTemplate) return;
    setDialogState('creating');
    setCreateError(null);
    createMutation.mutate({
      template_id: selectedTemplate.template_id,
      placeholder_values: placeholderValues,
      name: policyName.trim() || undefined,
    });
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-card rounded-lg shadow-xl border max-w-3xl w-full mx-4 max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            {dialogState === 'configure' && (
              <button
                onClick={handleBack}
                className="text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Back to template selection"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
            )}
            <div>
              <h2 className="text-lg font-semibold">
                {dialogState === 'select' ? 'Create from Template' : 'Configure Template'}
              </h2>
              <p className="text-sm text-muted-foreground">
                {dialogState === 'select'
                  ? 'Choose a policy template to get started quickly'
                  : selectedTemplate?.name}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Close dialog"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 overflow-auto flex-1">
          {dialogState === 'select' && (
            <>
              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : !templates || templates.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-sm text-muted-foreground">No templates available.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {templates.map((template) => (
                    <button
                      key={template.template_id}
                      onClick={() => handleSelectTemplate(template)}
                      className="text-left rounded-lg border bg-card p-4 hover:border-primary hover:bg-primary/5 transition-colors space-y-2"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <span className="font-medium text-sm leading-tight">{template.name}</span>
                        <div className="flex gap-1 shrink-0">
                          <CategoryBadge category={template.category} />
                          <EffectBadge effect={template.effect} />
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        {template.description}
                      </p>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}

          {(dialogState === 'configure' || dialogState === 'creating') && selectedTemplate && (
            <div className="space-y-5">
              {createError && (
                <div className="rounded bg-destructive/10 p-3 text-sm text-destructive">
                  {createError}
                </div>
              )}

              {/* Policy name */}
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Policy Name</label>
                <Input
                  value={policyName}
                  onChange={(e) => setPolicyName(e.target.value)}
                  placeholder={selectedTemplate.name}
                  disabled={dialogState === 'creating'}
                />
              </div>

              {/* Placeholder fields */}
              {selectedTemplate.placeholders.length > 0 && (
                <div className="space-y-4">
                  <p className="text-sm font-medium">Template Parameters</p>
                  {selectedTemplate.placeholders.map((placeholder) => (
                    <div key={placeholder.key} className="space-y-1.5">
                      <label className="text-sm font-medium">{placeholder.label}</label>
                      <Input
                        value={placeholderValues[placeholder.key] ?? ''}
                        onChange={(e) =>
                          setPlaceholderValues((prev) => ({
                            ...prev,
                            [placeholder.key]: e.target.value,
                          }))
                        }
                        placeholder={placeholder.hint}
                        disabled={dialogState === 'creating'}
                      />
                      <p className="text-xs text-muted-foreground">{placeholder.hint}</p>
                    </div>
                  ))}
                </div>
              )}

              {selectedTemplate.placeholders.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  This template requires no additional configuration.
                </p>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {(dialogState === 'configure' || dialogState === 'creating') && (
          <div className="p-4 border-t flex justify-end gap-2 shrink-0">
            <Button variant="outline" onClick={handleClose} disabled={dialogState === 'creating'}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={dialogState === 'creating'}>
              {dialogState === 'creating' ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Draft Policy'
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
